import { useCallback, useRef, useState, useEffect } from "react";
import { Loader2, Plus } from "lucide-react";
import type { TimelineProject, TimelineClip, TimelineAction } from "@scroll/shared-types/timeline";
import { getClipDuration, getClipStartTimes, getTotalDuration } from "@scroll/shared-types/timeline-utils";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { cancelUploadedClip, uploadTimelineClipFile } from "@/lib/timeline-upload";
import ClipThumbnailStrip from "./ClipThumbnailStrip";
import TrimHandles from "./TrimHandles";
import ImageClipResizeHandles from "./ImageClipResizeHandles";
import TransitionMarker from "./TransitionMarker";

// Practical client-upload cap. Vercel Blob accepts more, but anything larger
// is impractical to upload from a browser and usually indicates the user
// dragged in a source video they meant to transcode first.
const MAX_CLIP_BYTES = 512 * 1024 * 1024;

interface VideoTrackProps {
  project: TimelineProject;
  pixelsPerSecond: number;
  selectedClipId: string | null;
  onSelectClip: (id: string | null) => void;
  onDispatch: (action: TimelineAction) => void;
  canAddClips: boolean;
  canReorderClips: boolean;
  canEditTransitions: boolean;
}

function loadImageMetadata(url: string): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error("Image metadata could not be loaded"));
    img.src = url;
  });
}

function loadVideoMetadata(url: string): Promise<{
  duration: number;
  width: number;
  height: number;
}> {
  return new Promise((resolve, reject) => {
    const video = document.createElement("video");
    video.preload = "metadata";
    video.onloadedmetadata = () => resolve({
      duration: video.duration,
      width: video.videoWidth,
      height: video.videoHeight,
    });
    video.onerror = () => reject(new Error("Video metadata could not be loaded"));
    video.src = url;
  });
}

export default function VideoTrack({
  project,
  pixelsPerSecond,
  selectedClipId,
  onSelectClip,
  onDispatch,
  canAddClips,
  canReorderClips,
  canEditTransitions,
}: VideoTrackProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const blobUrlsRef = useRef<Set<string>>(new Set());
  const isMountedRef = useRef(true);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  // Slot semantics: 0 is "before clip 0", N is "between clip N-1 and clip N",
  // clips.length is "after the last clip". Lets the indicator render between
  // clips rather than on top of a single clip.
  const [dropSlot, setDropSlot] = useState<number | null>(null);
  const [isUploadingClip, setIsUploadingClip] = useState(false);

  // Revoke blob URLs on unmount and flag mount state so in-flight uploads
  // can detect they have nowhere to land and clean up their server blob.
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      for (const url of blobUrlsRef.current) {
        URL.revokeObjectURL(url);
      }
    };
  }, []);

  const handleAddMedia = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!canAddClips || isUploadingClip) return;
    const input = e.currentTarget;
    const file = e.target.files?.[0];
    if (!file) return;

    const isVideo = file.type.startsWith("video/");
    const isImage = file.type.startsWith("image/");
    if (!isVideo && !isImage) {
      toast({
        title: "Unsupported file",
        description: "Only video and image files can be added as clips.",
        variant: "destructive",
      });
      input.value = "";
      return;
    }
    if (file.size > MAX_CLIP_BYTES) {
      toast({
        title: "Clip is too large",
        description: "Timeline clips are capped at 512 MB. Compress the file and try again.",
        variant: "destructive",
      });
      input.value = "";
      return;
    }
    if (!user?.id) {
      toast({
        title: "Sign in required",
        description: "Please sign in before adding timeline clips.",
        variant: "destructive",
      });
      input.value = "";
      return;
    }

    const url = URL.createObjectURL(file);
    blobUrlsRef.current.add(url);
    setIsUploadingClip(true);

    try {
      const metadata = isImage
        ? { ...(await loadImageMetadata(url)), duration: 5 }
        : await loadVideoMetadata(url);
      const uploaded = await uploadTimelineClipFile(file, user.id);

      // The editor may have closed during upload. Clean up the orphan blob
      // rather than letting the new clip silently land in a detached project.
      if (!isMountedRef.current) {
        void cancelUploadedClip(uploaded.blobKey);
        return;
      }

      const baseClip = {
        id: uploaded.clipId,
        sourceUri: url,
        sourceWidth: metadata.width,
        sourceHeight: metadata.height,
        trimStart: 0,
        speed: 1,
        filter: null,
        blobKey: uploaded.blobKey,
        blobUrl: uploaded.blobUrl,
      };

      const clip: TimelineClip = isImage
        ? {
            ...baseClip,
            type: "image",
            sourceDuration: 5,
            trimEnd: 5,
            displayDuration: 5,
            volume: 0,
          }
        : {
            ...baseClip,
            type: "video",
            sourceDuration: metadata.duration,
            trimEnd: metadata.duration,
            displayDuration: 0,
            volume: 1,
          };

      onDispatch({ type: "ADD_CLIP", clip, atIndex: project.clips.length });
    } catch (err) {
      blobUrlsRef.current.delete(url);
      URL.revokeObjectURL(url);
      console.warn("[timeline-publish] clip upload failed", {
        fileType: file.type,
        fileSize: file.size,
        error: err instanceof Error ? err.message : String(err),
      });
      if (!isMountedRef.current) return;
      const reason = err instanceof Error ? err.message : "Please try adding that clip again.";
      toast({
        title: "Clip upload failed",
        description: reason,
        variant: "destructive",
      });
    } finally {
      if (isMountedRef.current) setIsUploadingClip(false);
      input.value = "";
    }
  }, [canAddClips, isUploadingClip, onDispatch, project.clips.length, toast, user?.id]);

  // Drag-and-drop reordering
  const handleDragStart = useCallback((index: number) => {
    if (!canReorderClips) return;
    setDraggingIndex(index);
  }, [canReorderClips]);

  const handleDragOver = useCallback(
    (e: React.DragEvent, hoverIndex: number) => {
      if (!canReorderClips) return;
      e.preventDefault();
      // Determine "before" or "after" by which half of the clip the cursor is over.
      const rect = e.currentTarget.getBoundingClientRect();
      const isAfter = e.clientX - rect.left > rect.width / 2;
      setDropSlot(isAfter ? hoverIndex + 1 : hoverIndex);
    },
    [canReorderClips],
  );

  const handleDrop = useCallback(() => {
    if (!canReorderClips || draggingIndex === null || dropSlot === null) {
      setDraggingIndex(null);
      setDropSlot(null);
      return;
    }
    // Removing the dragged item before inserting shifts indices to the right
    // of the source position down by one.
    const toIndex = dropSlot > draggingIndex ? dropSlot - 1 : dropSlot;
    if (toIndex !== draggingIndex) {
      onDispatch({ type: "REORDER_CLIPS", fromIndex: draggingIndex, toIndex });
    }
    setDraggingIndex(null);
    setDropSlot(null);
  }, [canReorderClips, draggingIndex, dropSlot, onDispatch]);

  const handleDragEnd = useCallback(() => {
    setDraggingIndex(null);
    setDropSlot(null);
  }, []);

  const totalDuration = getTotalDuration(project);
  const addBtnLeft = totalDuration * pixelsPerSecond + 8;

  // Precompute all clip start times in O(n)
  const clipStartTimes = getClipStartTimes(project);

  return (
    <div className="timeline-track">
      <span className="timeline-track-label">Video</span>
      {project.clips.map((clip, index) => {
        const startTime = clipStartTimes[index];
        const left = startTime * pixelsPerSecond;
        const isSelected = clip.id === selectedClipId;
        const isDragging = draggingIndex === index;

        return (
          <div
            key={clip.id}
            className={`timeline-clip-wrapper${isDragging ? " dragging" : ""}`}
            style={{ left: `${left}px` }}
            draggable={canReorderClips}
            onDragStart={() => handleDragStart(index)}
            onDragOver={(e) => handleDragOver(e, index)}
            onDrop={handleDrop}
            onDragEnd={handleDragEnd}
          >
            <ClipThumbnailStrip
              clip={clip}
              pixelsPerSecond={pixelsPerSecond}
              isSelected={isSelected}
              onClick={() => onSelectClip(clip.id)}
            />
            {isSelected && clip.type === "video" && (
              <TrimHandles
                clip={clip}
                pixelsPerSecond={pixelsPerSecond}
                onDispatch={onDispatch}
              />
            )}
            {isSelected && clip.type === "image" && (
              <ImageClipResizeHandles
                clip={clip}
                pixelsPerSecond={pixelsPerSecond}
                onDispatch={onDispatch}
              />
            )}
          </div>
        );
      })}

      {/* Drop indicator — a vertical line between clips where the dragged
       * clip will land. Hidden when the slot would be a no-op move. */}
      {draggingIndex !== null && dropSlot !== null && (() => {
        const effectiveTo = dropSlot > draggingIndex ? dropSlot - 1 : dropSlot;
        if (effectiveTo === draggingIndex) return null;
        let slotLeft: number;
        if (dropSlot >= project.clips.length) {
          const lastIndex = project.clips.length - 1;
          slotLeft = (clipStartTimes[lastIndex] + getClipDuration(project.clips[lastIndex])) * pixelsPerSecond;
        } else {
          slotLeft = clipStartTimes[dropSlot] * pixelsPerSecond;
        }
        return (
          <div
            className="timeline-clip-drop-indicator"
            style={{ left: `${slotLeft}px` }}
            aria-hidden="true"
          />
        );
      })()}

      {/* Transition markers between clips */}
      {canEditTransitions && project.clips.map((clip, index) => {
        if (index >= project.clips.length - 1) return null;
        const endTime = clipStartTimes[index] + getClipDuration(clip);
        const markerLeft = endTime * pixelsPerSecond;
        const transition = project.transitions[index] ?? {
          id: "",
          type: "cut" as const,
          duration: 0,
        };
        return (
          <TransitionMarker
            key={`t-${index}`}
            transition={transition}
            transitionIndex={index}
            left={markerLeft}
            onDispatch={onDispatch}
          />
        );
      })}

      {/* Add clip button */}
      {canAddClips && (
        <>
          <button
            className="timeline-add-clip-btn"
            style={{ left: `${addBtnLeft}px` }}
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploadingClip}
            title={isUploadingClip ? "Uploading clip" : "Add video or image"}
            aria-label={isUploadingClip ? "Uploading clip" : "Add video or image clip"}
            aria-busy={isUploadingClip}
          >
            {isUploadingClip ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*,image/*"
            onChange={handleAddMedia}
            className="hidden"
          />
        </>
      )}
    </div>
  );
}
